import { APIContext } from "../../types";
import { makePath } from "../../../url";
import * as APITypes from "../../generated-types/v1/Api";
import { getJSON, postJSON, putJSON } from "../../request";
import { applyPagination } from "../../pagination";
import { applyParams } from "../../../params";

export type VisitAPI = ReturnType<typeof createVisitSummaryAPI>;

type QueryParams = Partial<APITypes.VisitSummaryListByPatientParams>;

const DEFAULT_QUERY_PARAMS: QueryParams = {
  pageSize: 25,
};

function applyOptions(params: URLSearchParams, options: QueryParams) {
  applyParams(params, {
    visitDateBefore: options.visitDateBefore,
    visitDateAfter: options.visitDateAfter,
  });
}

export function createVisitSummaryAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
}: APIContext) {
  function querySummary(
    { patientId }: { patientId: string },
    options: QueryParams
  ) {
    const path = makePath(["api", "v1", "patient", patientId, "summary"]);
    const url = buildURL(path);

    const effectiveOptions = {
      ...DEFAULT_QUERY_PARAMS,
      ...options,
    };
    applyOptions(url.searchParams, effectiveOptions);
    applyPagination(url.searchParams, effectiveOptions);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.VisitSummary>(request);
  }

  function fetchSummary({
    patientId,
    summaryId,
  }: {
    patientId: string;
    summaryId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "summary",
      summaryId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.VisitSummary>(request);
  }

  function createSummary(
    { patientId }: { patientId: string },
    createRequest: APITypes.CreateVisitSummaryRequest
  ) {
    const path = makePath(["api", "v1", "patient", patientId, "summary"]);
    const url = buildURL(path);
    const request = postJSON(url, createRequest);
    return fetchJSON<APITypes.VisitSummary>(request);
  }

  function updateSummary(
    {
      patientId,
      summaryId,
    }: {
      patientId: string;
      summaryId: string;
    },
    replaceRequest: APITypes.ReplaceVisitSummaryRequest
  ) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "summary",
      summaryId,
    ]);
    const url = buildURL(path);
    const request = putJSON(url, replaceRequest);
    return fetchJSON<APITypes.VisitSummary>(request);
  }

  return {
    querySummary,
    createSummary,
    fetchSummary,
    updateSummary,
  };
}
